import { Button } from "@/components/flexkit/Button";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import { appMainService } from "@/ipc";
import { formatError } from "@/utils/errors";
import { useCallback, type FC } from "react";

export const DesktopSignin: FC = () => {
    const { toast } = useToast();
    const cpHost = window.location.origin;

    const handleConnectToGitpod = useCallback(async () => {
        const result = await appMainService!.signin({ host: cpHost });
        if (result === "timeout") {
            toast({ title: "Timed out", description: "Please try again" });
            return;
        }
        const parsed = parseSigninResult(result);
        if (!parsed) {
            toast({ title: "Unexpected parse error", description: "Please try again" });
            return;
        }
        try {
            // clear cookies first
            await fetch("/auth/oidc/logout", {
                method: "POST",
                credentials: "same-origin",
            });
        } catch {
            // explicitly ignore as we're about to login using the desktop flow
        }
        try {
            const response = await fetch("/auth/desktop/callback", {
                method: "POST",
                credentials: "same-origin",
                body: new URLSearchParams(parsed),
            });
            if (!response.ok) {
                throw new Error(response.statusText);
            }
            window.location.href = "/";
        } catch (error) {
            toast({ title: "Unexpected parse error", description: formatError(error) });
        }
    }, [cpHost, toast]);

    return (
        <div id="desktop-signin-container" className="flex flex-col gap-2">
            <div
                className="flex justify-center"
                onClick={handleConnectToGitpod}
                data-tracking-id="connect-to-ona-desktop-signin"
            >
                <Button id="signin" variant={"primary"} size={"lg"}>
                    Sign into Ona
                </Button>
            </div>
            <Text className="mb-2 text-center text-sm text-content-muted">Sign into {cpHost}</Text>
        </div>
    );
};

function parseSigninResult(result: string) {
    try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const asObject = JSON.parse(result);
        if (typeof asObject === "object" && "code" in asObject && "code_verifier" in asObject) {
            return {
                code: asObject.code as string,
                code_verifier: asObject.code_verifier as string,
            };
        }
    } catch (error) {
        console.error("error processing signin result", error);
    }
}
